import { POSType, ProductType } from '@digilize/shared/definitions/src';

import { Product, ProductPredefinedAmount, roundToCents } from './product';

/**
 * Whether this POS offers gift cards next to its subscriptions. Only the Prepaid online shop does,
 * mirroring the dashboard rule that fills its Products tab (`form-pos-add-ons.component.ts`): a
 * prepaid POS holds non-recurring subscriptions and gift cards, and the two may be bought together.
 * The Subscriptions online shop must not offer them — a recurring subscription is settled by SEPA
 * mandate and cannot share an order with a one-time product, which is why the POS types are split.
 */
export function posSellsGiftCards(posType: POSType): boolean {
  return posType === POSType.PrepaidOnline;
}

/**
 * The POS add-ons endpoint returns every product configured on the POS, so gift cards are picked out
 * by product type. The shop's own ProductType enum only names the handful of values the cinema flow
 * branches on, so the raw number from the API is compared against the shared enum instead.
 */
export function isGiftCardProduct(product: Product): boolean {
  return (product.type as number) === ProductType.Giftcard;
}

/**
 * Whether this gift card is sold at a list of predefined amounts rather than at a price of its own
 * (PAUL-3071). One product carries several amounts — the dashboard is configured with a single
 * "Giftcard" product whose price row is `0.00` and only holds the VAT rate — so such a card is sold
 * through a row per amount, and its own price is never what anything is bought at.
 *
 * `custom_value_supported` is deliberately not consulted: free amount entry stays a Cash Register
 * capability, so a prepaid POS that has it on is treated as mis-configured and still sells the list.
 */
export function sellsPredefinedAmounts(product: Product): boolean {
  return !!product.predefinedAmounts?.length;
}

/**
 * The amount off a `?amount=` link (`30`, `30.00`), read at the cent precision the product's own
 * amounts are compared at. Null for anything that is not a positive number — an empty, hand-edited
 * or localised (`30,00`) value — so a link nobody can honour falls through to the full offer instead
 * of reserving something the buyer never chose. The counterpart of `parseSubscriptionPeriodType`.
 */
export function parseGiftCardAmount(value: string | null): number | null {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed > 0 ? roundToCents(parsed) : null;
}

/** The amount on this product matching `value`, or null when it is not one of the amounts it sells. */
export function findPredefinedAmount(product: Product, value: number): ProductPredefinedAmount | null {
  return product.predefinedAmounts?.find((amount) => amount.value === value) ?? null;
}

/**
 * The other half of the same rule: a subscription on the POS product list. The subscription's own
 * details (billing periods, SEPA, sold out) never come from here — only its place in the product
 * groups does, and the catalogue it is matched against is `subscription/products`.
 */
export function isSubscriptionProduct(product: Product): boolean {
  return (product.type as number) === ProductType.Subscription;
}
